import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Formik, Form } from 'formik';
import {
  useTranslator,
  FormikInput as Input,
  FormPrompt,
  useAppContext,
  useToast,
  formatCalories,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import createValidationSchema from './validationSchema';
import TrackerFeedback from './TrackerFeedback';
import AmountWithUnitInput from './AmountWithUnitInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Ingredient } from '../types';
import { useGlobalIngredients } from '../hooks/useGlobalIngredients';
import { useGlobalIngredientsEdit } from '../hooks/useGlobalIngredientsEdit';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
const STORAGE_KEY_SHOULD_USE_DATA = 'tracker_should_use_data_from_session_storage';

// Conversion ratios
const FL_OZ_TO_ML_RATIO = 29.5735; // 1 US fl oz = 29.5735 ml
const OZ_TO_G_RATIO = 28.349523125; // 1 oz = 28.349523125 g
const CL_TO_ML_RATIO = 10; // 1 cl = 10 ml

// Helper function to normalize unit names
const normalizeUnit = (unit: string): string => {
  if (unit === 'grams') return 'g';
  if (unit === 'milliliters') return 'ml';
  if (unit === 'fl_oz' || unit === 'fluid ounces') return 'fl oz';
  return unit;
};

// Helper function to check if product is solid (weight-based)
const isSolidProduct = (unit: string): boolean => {
  const normalized = normalizeUnit(unit);
  return normalized === 'g' || normalized === 'oz';
};

// Helper function to check if product is liquid (volume-based)
const isLiquidProduct = (unit: string): boolean => {
  const normalized = normalizeUnit(unit);
  return normalized === 'ml' || normalized === 'cl' || normalized === 'fl oz';
};

// Get available units for a product based on its current unit and systemOfMeasuresNutrition
const getAvailableUnits = (currentUnit: string, appBrand: string): string[] => {
  const normalized = normalizeUnit(currentUnit);

  if (isSolidProduct(normalized)) {
    return ['g', 'oz'];
  }

  if (isLiquidProduct(normalized)) {
    if (appBrand === 'shestrong') {
      return ['ml', 'cl', 'fl oz'];
    } else {
      return ['ml', 'fl oz'];
    }
  }

  return [normalized];
};

const MIN_AMOUNT_AFTER_CONVERSION = 0.1;

// Convert amount from source unit to target unit
const convertAmount = (amount: number, sourceUnit: string, targetUnit: string): number => {
  const sourceNormalized = normalizeUnit(sourceUnit);
  const targetNormalized = normalizeUnit(targetUnit);

  if (sourceNormalized === targetNormalized) {
    return amount;
  }

  let converted: number;

  // Solid conversions (g ↔ oz)
  if (sourceNormalized === 'g' && targetNormalized === 'oz') {
    converted = amount / OZ_TO_G_RATIO;
  } else if (sourceNormalized === 'oz' && targetNormalized === 'g') {
    converted = amount * OZ_TO_G_RATIO;
  }
  // Liquid conversions: ml ↔ fl oz
  else if (sourceNormalized === 'ml' && targetNormalized === 'fl oz') {
    converted = amount / FL_OZ_TO_ML_RATIO;
  } else if (sourceNormalized === 'fl oz' && targetNormalized === 'ml') {
    converted = amount * FL_OZ_TO_ML_RATIO;
  }
  // cl ↔ fl oz (via ml)
  else if (sourceNormalized === 'cl' && targetNormalized === 'fl oz') {
    const mlAmount = amount * CL_TO_ML_RATIO;
    converted = mlAmount / FL_OZ_TO_ML_RATIO;
  } else if (sourceNormalized === 'fl oz' && targetNormalized === 'cl') {
    const mlAmount = amount * FL_OZ_TO_ML_RATIO;
    converted = mlAmount / CL_TO_ML_RATIO;
  }
  // cl ↔ ml
  else if (sourceNormalized === 'cl' && targetNormalized === 'ml') {
    converted = amount * CL_TO_ML_RATIO;
  } else if (sourceNormalized === 'ml' && targetNormalized === 'cl') {
    converted = amount / CL_TO_ML_RATIO;
  } else {
    return amount;
  }

  return Math.max(MIN_AMOUNT_AFTER_CONVERSION, converted);
};

interface TrackerEditMealPageProps {
  initialIngredients: Ingredient[];
  timestamp: number;
  mealName: string;
  requestId: string;
  mode: 'backward-navigation' | 'forward-navigation';
}

const TrackerEditMealPage: React.FC<TrackerEditMealPageProps> = ({
  initialIngredients,
  timestamp,
  mealName,
  requestId,
  mode,
}) => {
  const { t, language } = useTranslator();
  const decimalSeparator = language === 'pl-PL' ? ',' : '.';
  const navigate = useNavigate();
  const location = useLocation();
  const [isDirty, setIsDirty] = useState(false);
  const [localMealName, setLocalMealName] = useState(mealName);
  const { dietMealId } = useParams();

  const {
    consts: { APP_BRAND },
    images: { trashIcon, addIngredientIcon, editNeutralIcon, aiBadgeTiny, infoIcon },
  } = useAppContext();

  const { toastSuccess } = useToast();

  const isBarcode = window.location.search.includes('isBarcode=true');

  const [showModal, setShowModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleAiModal = () => {
    setShowAiModal(!showAiModal);
  };

  // Reset isDirty when returning to this view (location changes)
  // This ensures FormPrompt works correctly on subsequent visits
  useEffect(() => {
    setIsDirty(false);
  }, [location.key]);

  // Initialize ingredients from sessionStorage or from initial state
  // Store ingredients for 1 portion in sessionStorage
  const { updateIngredients: updateIngredientsConfirm } = useGlobalIngredients(initialIngredients); // for confirm button
  const { ingredients, updateIngredients } = useGlobalIngredientsEdit(initialIngredients);

  // Use state instead of useMemo to make multiplier reactive
  const [multiplier, setMultiplier] = useState(() => {
    return parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');
  });

  const [lastIngredientsCount, setLastIngredientsCount] = useState(ingredients.length);

  // Listen for changes in sessionStorage for tracker_multiplier
  useEffect(() => {
    const handleStorageChange = () => {
      const newMultiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');
      if (newMultiplier !== multiplier) {
        setMultiplier(newMultiplier);
      }
    };

    // Check for changes every 100ms (polling approach)
    // Note: storage event doesn't fire in the same tab, so we use polling
    const intervalId = setInterval(handleStorageChange, 100);

    return () => clearInterval(intervalId);
  }, [multiplier]);

  // Calculate formikValues based on ingredients from sessionStorage (stored for 1 portion)
  // Multiply by multiplier to display for current number of portions
  const formikValues = useMemo(() => {
    const values: { [key: string]: number | string | null } = {};

    values.mealName = localMealName;

    for (const ingredient of ingredients) {
      // Use measurements[1] if available, otherwise use measurements[0]

      let baseAmount = 0;
      let baseUnit = '';
      if (ingredient.measurements.length > 1) {
        baseAmount = ingredient.measurements?.[1]?.amount || 0;
        baseUnit = ingredient.measurements?.[1]?.unit || '';
      } else {
        baseAmount = ingredient.measurements?.[0]?.amount || 0;
        baseUnit = ingredient.measurements?.[0]?.unit || '';
      }

      values[ingredient.id] = Math.round(baseAmount * multiplier * 100) / 100;

      // Store the unit for each ingredient (default to current unit)
      const normalizedUnit = normalizeUnit(baseUnit);
      values[`${ingredient.id}_unit`] = normalizedUnit;

      // hack for barcode meal
      if (isBarcode) {
        if (
          (baseAmount === 100 &&
            (baseUnit === 'g' ||
              baseUnit === 'grams' ||
              baseUnit === 'ml' ||
              baseUnit === 'milliliters')) ||
          (baseAmount === 10 && baseUnit === 'cl') ||
          (baseAmount === 3 &&
            (baseUnit === 'oz' || baseUnit === 'fl_oz' || baseUnit === 'fluid ounces'))
        ) {
          values[ingredient.id] = null;
        }
      }
    }

    return values;
  }, [ingredients, multiplier, localMealName, isBarcode]);

  const validationSchema = useMemo(() => createValidationSchema(ingredients), [ingredients]);

  const calculateUpdatedIngredients = (values: { [key: string]: number | string }) => {
    return ingredients.map((ingredient) => {
      const formValue = values[ingredient.id];
      const selectedUnit = (values[`${ingredient.id}_unit`] as string) || null;

      if (
        formValue !== undefined &&
        typeof formValue === 'number' &&
        ingredient.measurements &&
        ingredient.measurements.length > 0
      ) {
        // Form value is already for 1 portion in sessionStorage, but user might have changed it
        // The displayed value is multiplied by multiplier, so we need to divide to get 1 portion value
        const amountPer1Portion = formValue / multiplier;

        // Get current stored measurement
        const measurementIndex = ingredient.measurements.length > 1 ? 1 : 0;
        const currentMeasurement = ingredient.measurements[measurementIndex];
        const currentStoredAmount = currentMeasurement.amount;
        const originalUnit = normalizeUnit(currentMeasurement.unit);

        // Determine which unit to use for storage (selected unit if changed, otherwise original)
        const unitToStore =
          selectedUnit && selectedUnit !== originalUnit ? selectedUnit : originalUnit;

        // If unit was changed, we need to convert back to original unit for ratio calculation
        // but we'll store with the selected unit
        let amountForRatioCalculation = amountPer1Portion;
        if (selectedUnit && selectedUnit !== originalUnit) {
          // Convert from selected unit back to original unit for ratio calculation
          amountForRatioCalculation = convertAmount(amountPer1Portion, selectedUnit, originalUnit);
        }

        const ratio = currentStoredAmount > 0 ? amountForRatioCalculation / currentStoredAmount : 1;

        if (ingredient.measurements.length > 1) {
          // Two measurements case
          return {
            ...ingredient,
            kcal: Math.round(ingredient.kcal * ratio),
            measurements: [
              {
                ...ingredient.measurements[0],
                amount: Math.round(ingredient.measurements[0].amount * ratio * 100) / 100,
              },
              {
                ...ingredient.measurements[1],
                amount: amountPer1Portion,
                unit: unitToStore, // Store with selected unit if changed
              },
            ],
            macro: ingredient.macro
              ? {
                  protein: {
                    grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                  },
                  fat: {
                    grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                  },
                  carbohydrates: {
                    grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                  },
                }
              : undefined,
          };
        } else {
          // Single measurement case
          return {
            ...ingredient,
            kcal: Math.round(ingredient.kcal * ratio),
            measurements: [
              {
                ...ingredient.measurements[0],
                amount: amountPer1Portion,
                unit: unitToStore, // Store with selected unit if changed
              },
            ],
            macro: ingredient.macro
              ? {
                  protein: {
                    grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                  },
                  fat: {
                    grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                  },
                  carbohydrates: {
                    grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                  },
                }
              : undefined,
          };
        }
      }
      return ingredient;
    });
  };

  const handleSubmit = (values: { [key: string]: number | string }) => {
    setIsDirty(false);
    // Set flag to use data from sessionStorage on next load
    sessionStorage.setItem(STORAGE_KEY_SHOULD_USE_DATA, 'true');
    // Save meal name to sessionStorage
    sessionStorage.setItem('tracker_meal_name', localMealName);
    // Values from form are for current portions, we need to divide by multiplier to store for 1 portion
    const updatedIngredients = calculateUpdatedIngredients(values);

    // Update ingredients in both storages (stored for 1 portion)
    // Update tracker_ingredients (for confirm button - main storage)
    updateIngredientsConfirm(updatedIngredients);
    // Also update tracker_ingredients_edit to keep them in sync
    updateIngredients(updatedIngredients);

    // navigate to add meal page
    setTimeout(() => {
      if (mode === 'forward-navigation') {
        navigate(`/tracker/describe-meal/meal-details/${dietMealId}`);
      } else {
        navigate(-1);
      }
    }, 0);
  };

  const handleAddIngredientBack = (deletedIngredient: Ingredient) => {
    // Use functional update to get the current state of ingredients
    updateIngredients((currentIngredients: Ingredient[]) => {
      // Check if ingredient is already in the list to avoid duplicates
      const alreadyExists = currentIngredients.some(
        (ingredient) => ingredient.id === deletedIngredient.id
      );
      if (alreadyExists) {
        return currentIngredients;
      }
      return [...currentIngredients, deletedIngredient];
    });
  };

  // Just mark form as dirty when values change
  const handleFormikChange = useCallback(() => {
    setIsDirty(true);
  }, []);

  // Handle unit change - convert amount to new unit
  const handleUnitChange = useCallback(
    (
      ingredientId: string,
      newUnit: string,
      setFieldValue: (field: string, value: number | string) => void,
      values: { [key: string]: number | string | null }
    ) => {
      setIsDirty(true);

      const currentAmount = values[ingredientId];
      const currentUnit = (values[`${ingredientId}_unit`] as string) || '';

      if (typeof currentAmount === 'number' && currentUnit && newUnit !== currentUnit) {
        // Current amount is already multiplied by multiplier for display
        // Convert amount from current unit to new unit
        const convertedAmount = convertAmount(currentAmount, currentUnit, newUnit);
        const roundedAmount = Math.round(convertedAmount * 100) / 100;

        // Update both amount and unit
        setFieldValue(ingredientId, roundedAmount);
        setFieldValue(`${ingredientId}_unit`, newUnit);
      } else {
        // Just update the unit
        setFieldValue(`${ingredientId}_unit`, newUnit);
      }
    },
    []
  );

  const handleAddIngredient = (values: { [key: string]: number | string }) => {
    const updatedIngredients = calculateUpdatedIngredients(values);

    // Update ingredients in sessionStorage (stored for 1 portion)
    updateIngredients(updatedIngredients);

    // Set flag to use data from sessionStorage when returning
    sessionStorage.setItem('tracker_should_use_data_from_session_storage', 'true');

    navigate('add-ingredient');
  };

  const shortenUnit = (unit: string) => {
    if (unit === 'grams') {
      return 'g';
    } else if (unit === 'milliliters') {
      return 'ml';
    } else if (unit === 'oz') {
      return 'oz';
    } else if (unit === 'fl_oz') {
      return 'fl oz';
    } else {
      return unit;
    }
  };

  const handleSaveMealName = (values: { [key: string]: number | string | null }) => {
    setIsDirty(true);
    const mealName = (values.mealName as string) || '';
    setLocalMealName(mealName);
    toggleModal();
  };

  const fakeSubmit = () => {
    // blur input
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.ingredients.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={fakeSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        key={location.key}
      >
        {({ isValid, values, setFieldValue }) => {
          // Check if all ingredient fields are !== null
          const allFieldsValid = ingredients.every((ingredient) => {
            const value = values[ingredient.id];
            return value !== null && value !== undefined;
          });

          // Combine Formik's isValid with our custom null check
          const isFormValid = isValid && allFieldsValid;

          // Calculate display ingredients with updated measurements[0] based on current form values
          const displayIngredients = ingredients.map((ingredient) => {
            const formValue = values[ingredient.id];
            const selectedUnit = (values[`${ingredient.id}_unit`] as string) || null;

            if (
              formValue !== undefined &&
              typeof formValue === 'number' &&
              ingredient.measurements
              //ingredient.measurements.length > 1
            ) {
              // Calculate ratio based on form value change
              let amountPer1Portion = formValue / multiplier;

              // Get current stored measurement
              const measurementIndex = ingredient.measurements.length > 1 ? 1 : 0;
              const currentMeasurement = ingredient.measurements[measurementIndex];
              const currentStoredAmount = currentMeasurement.amount;
              const originalUnit = normalizeUnit(currentMeasurement.unit);

              // If unit was changed, we need to convert back to original unit before calculating ratio
              // This ensures that ratio is calculated correctly (same unit comparison)
              if (selectedUnit && selectedUnit !== originalUnit) {
                // Convert from selected unit back to original unit
                amountPer1Portion = convertAmount(amountPer1Portion, selectedUnit, originalUnit);
              }

              const ratio = currentStoredAmount > 0 ? amountPer1Portion / currentStoredAmount : 1;

              // Update measurements[0] for display only
              return {
                ...ingredient,
                measurements:
                  ingredient.measurements.length > 1
                    ? [
                        {
                          ...ingredient.measurements[0],
                          amount: Math.round(ingredient.measurements[0].amount * ratio * 100) / 100,
                        },
                        ingredient.measurements[1],
                      ]
                    : [ingredient.measurements[0]],

                kcal: Math.round(ingredient.kcal * ratio),
                macro: ingredient.macro
                  ? {
                      protein: {
                        grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                      },
                      fat: {
                        grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                      },
                      carbohydrates: {
                        grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                      },
                    }
                  : undefined,
              };
            }
            return ingredient;
          });

          const handleDeleteIngredientWithValues = async (id: string) => {
            setIsDirty(true);
            // First, calculate updated ingredients based on current form values
            const ingredientsBasedOnFormValues = calculateUpdatedIngredients(
              values as { [key: string]: number | string }
            );

            // Find the ingredient to delete (for undo functionality)
            const deletedIngredient = ingredientsBasedOnFormValues.find(
              (ingredient) => ingredient.id === id
            );

            // Remove the ingredient from the updated list
            const updatedIngredients = ingredientsBasedOnFormValues.filter(
              (ingredient) => ingredient.id !== id
            );

            // Update ingredients in sessionStorage
            await updateIngredients(updatedIngredients);

            if (deletedIngredient) {
              toastSuccess(
                <p className="w-100">
                  {t('tracker.logMeal.ingredients.deleteConfirmation.description')}

                  <Button
                    color="link"
                    className="float-end"
                    onClick={() => handleAddIngredientBack(deletedIngredient)}
                  >
                    {t('tracker.logMeal.ingredients.deleteConfirmation.button')}
                  </Button>
                </p>
              );
            }
          };

          // needed to last ingredient animation to work
          setTimeout(() => {
            setLastIngredientsCount(displayIngredients.length);
          }, 1000);

          return (
            <Form>
              <FormPrompt
                hasUnsavedChanges={isDirty}
                keyword={mode === 'backward-navigation' ? 'edit-meal' : dietMealId}
                mode={mode === 'backward-navigation' ? 'back' : 'exit'}
                key={`${dietMealId}-${mode}-${location.key}`}
              >
                {({
                  showModal,
                  toggleModal,
                  handleClick,
                }: {
                  showModal: boolean;
                  toggleModal: () => void;
                  handleClick: () => void;
                }) => (
                  <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
                    <ModalHeader toggle={toggleModal} />
                    <ModalBody className="pt-0">
                      <h2 className="title-medium semi-bold text-center">
                        {t('tracker.logMeal.exitPopup.title')}
                      </h2>
                      <p className="text-center">{t('tracker.logMeal.exitPopup.description')}</p>

                      <Button
                        color="secondary"
                        className="w-100 mb-s"
                        outline
                        onClick={toggleModal}
                      >
                        {t('tracker.logMeal.exitPopup.backButton')}
                      </Button>

                      <Button color="secondary" className="w-100" outline onClick={handleClick}>
                        {t('tracker.logMeal.exitPopup.exitButton')}
                      </Button>
                    </ModalBody>
                  </Modal>
                )}
              </FormPrompt>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <h2 className="title-large semi-bold mt-xxs mb-l">
                            <Button color="link" onClick={toggleModal} className="float-end p-0">
                              <img src={editNeutralIcon} alt="edit" className="ms-1" />
                            </Button>
                            {localMealName}
                          </h2>

                          {mode === 'forward-navigation' && (
                            <>
                              <div className="d-flex align-items-center mt-xl ">
                                <img
                                  src={aiBadgeTiny}
                                  alt=""
                                  className="me-s"
                                  style={{ width: '39px' }}
                                />
                                <p className="mb-0 description text-text-variant-color">
                                  {t('tracker.aiSignature')}
                                </p>
                                <img
                                  src={infoIcon}
                                  className="ms-s"
                                  style={{ width: '20px' }}
                                  onClick={toggleAiModal}
                                  alt=""
                                />
                              </div>

                              <hr className="mb-0" />
                            </>
                          )}

                          <ul className="tracker-ingredients-list-items px-0">
                            {displayIngredients.map((ingredient: Ingredient, index: number) => (
                              <li
                                key={index}
                                className={
                                  displayIngredients.length > lastIngredientsCount
                                    ? 'tracker-ingredient-item pt-s pb-0 enable-animation'
                                    : 'tracker-ingredient-item pt-s pb-0'
                                }
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div
                                    className="d-flex flex-column justify-content-start mb-m"
                                    style={{ maxWidth: '60%' }}
                                  >
                                    <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                      {ingredient.name}
                                      {ingredient.brand && ` (${ingredient.brand})`}
                                    </span>
                                  </div>
                                  <span className="text-text-main-color semi-bold d-flex align-items-center">
                                    {(() => {
                                      // Determine which measurement to use
                                      let currentMeasurement;
                                      if (ingredient.measurements.length > 1) {
                                        currentMeasurement = ingredient.measurements[1];
                                      } else {
                                        currentMeasurement = ingredient.measurements[0];
                                      }

                                      const originalUnit = normalizeUnit(currentMeasurement.unit);
                                      const selectedUnit =
                                        (values[`${ingredient.id}_unit`] as string) || originalUnit;

                                      // Check if product supports unit selection
                                      const isSupportedUnit =
                                        isSolidProduct(originalUnit) ||
                                        isLiquidProduct(originalUnit);

                                      if (!isSupportedUnit) {
                                        // For unsupported units, show input without select
                                        return (
                                          <>
                                            <Input
                                              name={ingredient.id}
                                              type="number"
                                              unit={shortenUnit(originalUnit)}
                                              translateErrMsg={true}
                                              step={0.1}
                                              callbackOnChange={handleFormikChange}
                                            />
                                            <Button
                                              color="link"
                                              type="button"
                                              className="mb-m pe-0"
                                              onClick={() =>
                                                handleDeleteIngredientWithValues(ingredient.id)
                                              }
                                            >
                                              <img src={trashIcon} alt="delete" />
                                            </Button>
                                          </>
                                        );
                                      }

                                      // Get available units for this product
                                      const availableUnits = getAvailableUnits(
                                        originalUnit,
                                        APP_BRAND as string
                                      );

                                      return (
                                        <>
                                          <AmountWithUnitInput
                                            amountName={ingredient.id}
                                            unitName={`${ingredient.id}_unit`}
                                            availableUnits={availableUnits}
                                            currentUnit={selectedUnit}
                                            onUnitChange={(newUnit) =>
                                              handleUnitChange(
                                                ingredient.id,
                                                newUnit,
                                                setFieldValue,
                                                values
                                              )
                                            }
                                            onAmountChange={handleFormikChange}
                                            step={0.1}
                                          />
                                          <Button
                                            color="link"
                                            type="button"
                                            className="mb-m pe-0"
                                            onClick={() =>
                                              handleDeleteIngredientWithValues(ingredient.id)
                                            }
                                          >
                                            <img src={trashIcon} alt="delete" />
                                          </Button>
                                        </>
                                      );
                                    })()}
                                  </span>
                                </div>
                                <div className="mb-s">
                                  <span className="description text-text-variant-2-color d-flex justify-content-between align-items-center">
                                    <span>
                                      {formatCalories(ingredient.kcal * multiplier)} kcal
                                      <span className="d-inline-block ms-3">
                                        {t('summary.macros.proteinsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.protein?.grams ?? 0) *
                                              multiplier *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.fatsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.fat?.grams ?? 0) * multiplier * 10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.carbsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.carbohydrates?.grams ?? 0) *
                                              multiplier *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                    </span>
                                    <span style={{ paddingRight: '28px' }}>
                                      {ingredient.measurements &&
                                        ingredient.measurements[0].unit !== 'milliliters' &&
                                        ingredient.measurements[0].unit !== 'cl' &&
                                        ingredient.measurements[0].unit !== 'ml' &&
                                        ingredient.measurements[0].unit !== 'oz' &&
                                        ingredient.measurements[0].unit !== 'fl oz' &&
                                        ingredient.measurements[0].unit !== 'fl_oz' &&
                                        ingredient.measurements[0].unit !== 'g' &&
                                        ingredient.measurements[0].unit !== 'grams' && (
                                          <>
                                            ≈
                                            <MeasurementUnits
                                              measurements={[ingredient.measurements[0]]}
                                              multiplier={multiplier}
                                              className="ms-1"
                                            />
                                          </>
                                        )}
                                    </span>
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>

                          <hr className="mt-0" />
                          <div className="d-flex justify-content-center">
                            <Button
                              color="link"
                              onClick={() =>
                                handleAddIngredient(values as { [key: string]: number | string })
                              }
                            >
                              <img src={addIngredientIcon} className="me-1" alt="add" />
                              <span className="semi-bold">
                                {t('tracker.logMeal.ingredients.addIngredientButton')}
                              </span>
                            </Button>
                          </div>

                          {mode === 'forward-navigation' && (
                            <TrackerFeedback requestId={requestId ?? ''} />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Button
                          type="button"
                          onClick={() => handleSubmit(values as { [key: string]: number | string })}
                          disabled={!isFormValid}
                          color="primary"
                          className="w-100"
                        >
                          {t('tracker.logMeal.ingredients.confirmButton')}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </div>

              <Modal
                className="from-bottom"
                isOpen={showModal}
                fullscreen={true}
                size="lg"
                toggle={toggleModal}
              >
                <div className="change-meal-name">
                  <Container>
                    <p className="small medium text-center">
                      {t('tracker.logMeal.mealName.edit.title')}
                    </p>
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Input
                          name="mealName"
                          label={t('tracker.logMeal.mealName.edit.inputLabel')}
                          type="text"
                          translateErrMsg
                        />
                        <Row>
                          <Col xs="6">
                            <Button
                              type="button"
                              color="link"
                              className="w-100"
                              onClick={toggleModal}
                            >
                              {t('tracker.logMeal.edit.cancelButton')}
                            </Button>
                          </Col>
                          <Col xs="6">
                            <Button
                              type="button"
                              color="primary"
                              className="w-100"
                              onClick={() => handleSaveMealName(values)}
                            >
                              {t('tracker.logMeal.mealName.edit.saveButton')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </Modal>
            </Form>
          );
        }}
      </Formik>

      <Modal isOpen={showAiModal} toggle={toggleAiModal} centered size="md">
        <ModalHeader toggle={toggleAiModal} />
        <ModalBody className="pt-0">
          <h2 className="title-medium semi-bold">{t('tracker.aiSignature.popup.title')}</h2>
          <p>{t('tracker.aiSignature.popup.description')}</p>

          <Button color="primary" className="w-100" onClick={toggleAiModal}>
            {t('tracker.aiSignature.popup.button')}
          </Button>
        </ModalBody>
      </Modal>
    </Layout>
  );
};

export default TrackerEditMealPage;
